/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { isValidElement, ReactElement } from 'react';
import { HTMLProps } from 'react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { Content, ContentProps } from '../../patterns/content';
import { cn } from '@ui/lib';

// @Deprecated: Use children instead.
interface RadioWithContentPropsTypeWithContent {
  paddingOn?: boolean;
  usedInAForm?: boolean;
  value: string;
  content: ContentProps;
}

interface RadioWithContentPropsType {
  paddingOn?: boolean;
  usedInAForm?: boolean;
  value: string;
  children: ReactElement | ReactElement<typeof Content>;
}

export type RadioWithContentProps = (
  | RadioWithContentPropsTypeWithContent
  | RadioWithContentPropsType
) &
  Omit<HTMLProps<HTMLDivElement>, 'content'>;

// Custom RadioButton indicator component
const RadioIndicator = () => (
  // Verify subtle margin-top is needed to align the indicator slightly above the text
  <RadixRadioGroup.Indicator className="size-4 absolute left-xs top-xs rounded-[30px] bg-primary" />
);

export const RadioWithContent = ({
  paddingOn = false,
  usedInAForm = false,
  value,
  ...rest
}: RadioWithContentProps) => {
  let containerClassName =
    'flex w-full flex-col items-start justify-start cursor-pointer';

  if (paddingOn && !usedInAForm) {
    containerClassName +=
      ' inline-flex h-52 w-[375px] flex-col items-start justify-start gap-4 rounded-[5px] border p-4 ';
  } else if (paddingOn && usedInAForm) {
    containerClassName += ' px-4';
  } else if (!paddingOn && usedInAForm) {
    containerClassName += ' flex-row items-start gap-3';
  }

  const content = 'content' in rest ? rest.content : undefined;
  const { children, ...props } = rest;

  return (
    <div
      {...props}
      content={undefined}
      className={cn(
        'inline-flex flex-row items-start gap-4',
        containerClassName,
      )}
    >
      <RadixRadioGroup.Item
        value={value}
        className="size-6 relative flex-shrink-0"
        id={`radio-${value}`}
      >
        <div className="size-6 absolute left-0 top-0 rounded-[32px] border-2 border-shadow" />
        <RadioIndicator />
      </RadixRadioGroup.Item>
      {content && <Content {...content} />}
      {children}
    </div>
  );
};

// @Deprecated: Use RadioWithContentGroupProps instead.
export interface RadioWithContentGroupWithOptionsProps
  extends RadixRadioGroup.RadioGroupProps {
  options: Array<{ value: string; content: ContentProps }>;
}

export interface RadioWithContentGroupProps
  extends RadixRadioGroup.RadioGroupProps {
  options: Array<{
    value: string;
    content: ReactElement | ReactElement<typeof Content>;
  }>;
}

export const RadioWithContentGroup = ({
  value,
  onValueChange,
  options,
  ...props
}: RadioWithContentGroupProps | RadioWithContentGroupWithOptionsProps) => {
  return (
    <RadixRadioGroup.Root
      value={value as string}
      onValueChange={onValueChange}
      {...props}
      className="flex w-full flex-col items-start gap-6"
    >
      {options.map((option, i) =>
        isValidElement(option.content) ? (
          <RadioWithContent usedInAForm key={i} value={option.value}>
            {option.content}
          </RadioWithContent>
        ) : (
          <RadioWithContent
            usedInAForm
            key={i}
            value={option.value}
            content={option.content as ContentProps}
          />
        ),
      )}
    </RadixRadioGroup.Root>
  );
};
